// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CitedClaimsPanel.test.tsx
// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/CommentCitationLinks.test.tsx
// covered-by: frontends/carrie/src/routes/tickets/routes/components/comment-drawer/__tests__/Comment.test.tsx
// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { ArrowSquareOut } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Preview } from "./Preview";
import { SpreadsheetView } from "./SpreadsheetView";

import { useObjectUrl } from "../hooks/useObjectUrl";
import { citationAnchorParam } from "../routes/settings/routes/knowledge-database/citationAnchor";
import { PdfCitationPreview } from "../routes/settings/routes/knowledge-database/components/PdfCitationPreview";
// `search`, never `route`: `route.ts` re-exports both of these names, but it
// also calls `createRoute({...})` at module scope and imports `./view`, so
// reaching for them there evaluates the entire Knowledge Database page and
// registers its route. This dialog renders inside the tickets comment drawer
// and the Ask Puntt Cited Claims rail, so that import pulled the whole admin
// page into both. `search.ts` is the leaf both constants actually live in and
// says so in its own docstring.
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../routes/settings/routes/knowledge-database/search";
import {
  type TicketMessageCitation,
  cellAnchorLabel,
  isKnowledgeRuleCitation,
} from "../utils/citations";
import { useSourceFileQuery } from "../utils/queries/knowledge";

/**
 * Shows the cited REGION of the source, not just its name: a PDF page with the
 * bbox/pin, or the cited sheet scrolled to its cell range. Both reuse the
 * renderers the Knowledge Database admin already uses for the same citation
 * (`PdfCitationPreview`, `SpreadsheetView` via `SourceViewerDialog`), so a rule
 * looks the same wherever it is cited from.
 *
 * why the spreadsheet arm matters more than it sounds: a claims registry is a
 * WORKBOOK. Every rule Danone holds is a row in one, so the PDF-only version of
 * this rendered nothing at all for the entire corpus -- the reader got a claim
 * and a file name and no way to check either against the source.
 *
 * Unanchored citations and download failures still fall back silently to the
 * quote-only view above: this is a progressive enhancement, never a blocker.
 * Its own component so `useSourceFileQuery` and `useObjectUrl` stay
 * unconditional hooks.
 */
export function KnowledgeRuleCitationPin({
  sourceId,
  pageNumber,
  bbox,
  relX,
  relY,
  cellAnchor,
  testIdPrefix = "comment-knowledge-rule-citation",
  sheetClassName = "h-[70vh] min-h-0",
}: {
  sourceId: string;
  /** Scopes every testid, since two comments' sources can be open at once. */
  testIdPrefix?: string;
  /** The sheet's box: a dialog gives it most of the viewport, an inline preview far less. */
  sheetClassName?: string;
  pageNumber?: number;
  bbox?: { x: number; y: number; w: number; h: number };
  relX?: number;
  relY?: number;
  cellAnchor?: {
    sheetIndex?: number;
    sheetName?: string;
    range?: string;
    row?: number;
  };
}) {
  const { data, isLoading } = useSourceFileQuery(sourceId);
  const mimeType = data?.mimeType ?? "";
  const isSpreadsheet =
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv" ||
    mimeType === "application/vnd.ms-excel";
  // SpreadsheetView fetches by URL, so the cached bytes are handed to it as an
  // in-memory object URL rather than a second presigned download.
  const objectUrl = useObjectUrl(
    isSpreadsheet ? data?.bytes : undefined,
    mimeType,
  );
  // why memoized: a fresh object each render re-runs SpreadsheetView's
  // citation-bounds memo and with it the scroll effect, which yanks the sheet
  // back to the cited cell every time anything re-renders -- so a reader who
  // scrolls away is dragged back while they read. Same defect the sibling
  // viewer already fixed (SourceViewerDialog.tsx). Declared above the early
  // returns below because a hook cannot sit behind one.
  const highlight = useMemo(
    () =>
      cellAnchor
        ? {
            sheetIndex: cellAnchor.sheetIndex,
            sheetName: cellAnchor.sheetName,
            range: cellAnchor.range,
            row: cellAnchor.row,
          }
        : null,
    [
      cellAnchor?.sheetIndex,
      cellAnchor?.sheetName,
      cellAnchor?.range,
      cellAnchor?.row,
    ],
  );
  const { t } = useTranslation("ui");
  if (isLoading) {
    return (
      <Text
        size="1"
        className="text-puntt-neutral-gray-11"
        data-testid={`${testIdPrefix}-pin-loading`}
      >
        {t("commentCitationLinks.loadingSource")}
      </Text>
    );
  }
  if (!data) return null;
  if (isSpreadsheet) {
    // A workbook citation without a cell range still opens the sheet: seeing
    // the source beats seeing its file name, and the range is what the
    // highlight needs, not what the view needs.
    if (!objectUrl) return null;
    return (
      // why the height: SpreadsheetView is `flex size-full flex-col` around a
      // `flex-1 overflow-auto` scroller, so in an auto-height wrapper the sheet
      // renders at full length, nothing scrolls, and its scroll-to-the-cited-
      // cell effect has no container to move -- it highlights the right row and
      // never shows it. Matches the sibling viewer (SourceViewerDialog.tsx).
      <div data-testid={`${testIdPrefix}-sheet`} className={sheetClassName}>
        <SpreadsheetView url={objectUrl} highlight={highlight} />
      </div>
    );
  }
  // A failed download or a type neither viewer reads: keep the quote-only
  // citation rather than showing a broken viewer.
  if (mimeType !== "application/pdf" || pageNumber == null) return null;
  return (
    <PdfCitationPreview
      data={data.bytes}
      pageNumber={pageNumber}
      bbox={bbox ?? null}
      relX={relX}
      relY={relY}
      testIdPrefix={testIdPrefix}
    />
  );
}

/**
 * Shared Knowledge Database rule citation dialog — statement headline, quote
 * excerpt, and deep link into the Rules tab. Used by comment chips and the
 * Cited Claims rail so both surfaces stay in sync.
 *
 * WHY IT LIVES IN `src/components/` AND NOT UNDER A ROUTE: it is now rendered
 * from two different route trees -- the tickets comment drawer
 * (`routes/tickets/.../comment-drawer/Comment.tsx`) and Ask Puntt
 * (`ChatMessage.tsx`, `CitedClaimsPanel.tsx`, `KnowledgeRuleCitationLink.tsx`).
 * Leaving it under `routes/ask-puntt/components/` would make the drawer import
 * one route's private module to render a ticket comment, which is the inversion
 * the lint wall names when it says "Share via src/components, src/utils,
 * src/config, or packages" (`.eslintrc.json`). The move is deliberate.
 *
 * It still reads four things that live under
 * `routes/settings/routes/knowledge-database/`. Three are leaf modules (types
 * and constants -- no component, no `createRoute`): `citationAnchorParam`,
 * `KNOWLEDGE_DATABASE_PATH` and `KNOWLEDGE_DATABASE_DEFAULT_SEARCH`. Shared
 * code already depends on those exactly this way -- `AppSidebar.tsx`,
 * `ClaimCitationLink.tsx` and `ask-puntt-review/claimCitations.ts` all sit in
 * `src/components/` and all import them from the same place. The fourth,
 * `PdfCitationPreview`, is a pure presentational renderer, and reusing it is
 * the whole point of this dialog: a rule must look the same wherever it is
 * cited from, so a second copy here would be the drift this component exists to
 * prevent.
 *
 * `src/utils` / `src/components` is the right eventual home for all four, but
 * that promotion rewrites every call site above plus both in-route users of
 * `PdfCitationPreview` and their tests, and belongs in its own change rather
 * than in a citation-payload fix. The one import that was genuinely harmful --
 * the non-leaf `./route` module, which dragged the whole admin page and its
 * route registration into the comment drawer -- is gone above.
 */
export function KnowledgeRuleCitationDialog({
  citation,
  open,
  onOpenChange,
  children,
  showKnowledgeDatabaseLink = true,
  allowSourcePreview = true,
}: {
  citation: TicketMessageCitation;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional Dialog.Trigger (or other) children. Omit for controlled-only open. */
  children?: ReactNode;
  /**
   * Share-link guests cannot open the Knowledge Database settings route, so
   * the deep link is hidden rather than offered as a dead end. Like
   * `allowSourcePreview` below this only removes the affordance; the route
   * itself is what refuses the guest.
   */
  showKnowledgeDatabaseLink?: boolean;
  /**
   * PRESENTATIONAL GATE, NOT A SECRECY BOUNDARY. Hides everything that shows
   * the underlying Knowledge Database *source*: the pinned PDF page, the
   * source document name, the sheet/cell anchor, and the verbatim `quote`
   * excerpt. Off for share-link guests, who hold a ticket token but no
   * Knowledge Database access.
   *
   * What it does NOT do is keep those fields away from the guest — React
   * cannot, since the payload arrives before this component runs. And nothing
   * upstream does it either: the per-reader redaction pass was removed in
   * #3441, `sanitizeComment` (`services/christo/lib/services/ticket.ts`) has no
   * guest branch, and `getTicketComments`
   * (`services/christo/app/handlers/catalyst/comments.ts`) calls it with no
   * reader signal — christo's own
   * `__tests__/comments-guest-redaction.integration.spec.ts` pins that a guest
   * receives the unredacted body. A guest can therefore still read the excerpt,
   * the source name and the anchor off the network response. This flag is the
   * ONLY layer, and it is presentational: "do not put the source in front of a
   * guest", never "the guest cannot obtain it".
   *
   * WHAT THIS CHANGE WIDENED. christo's `CitationCommonSchema`
   * (`services/christo/lib/models/ticket.ts`) now declares `statement`,
   * `sourceName`, `brand` and the nested `location`, so that a comment write
   * stops dropping the claim and its pinned source. Mongoose strips undeclared
   * paths on hydrate as well as on save, so before those declarations
   * `sourceName` and `location.cellAnchor` never reached any reader — `quote`
   * was already declared, and was already readable. The source document NAME
   * and the sheet/page anchor are therefore net-new guest-readable data, and
   * that exposure is accepted and unmitigated on this change; the same note is
   * on the schema. Closing it means adding a server-side strip in christo, not
   * tightening anything here.
   *
   * Any such strip has to be scoped to KNOWLEDGE-RULE citations: `quote` and
   * `location` also carry the review-file pin a guest is supposed to see, so a
   * blanket strip over every citation kind would blank out comment pins on the
   * shared file. This gate is scoped the same way — it runs only on rule
   * citations.
   *
   * `quote` is hidden by this gate and `statement` is not: rembrandt writes
   * `quote -> verbatim source evidence` (the raw cell/line lifted out of the
   * source document) while `statement` is the rewritten, reader-facing claim —
   * "never the raw source cell (that's `quote`)"
   * (`services/rembrandt/services/review/knowledge_rule_search.py`). So the UI
   * shows a guest the claim the comment already told them and does not display
   * the source text it was read from — while the payload still carries it.
   */
  allowSourcePreview?: boolean;
}) {
  const { t } = useTranslation("ui");
  if (!isKnowledgeRuleCitation(citation)) return null;

  const statement = citation.statement?.trim();
  const quote = allowSourcePreview ? citation.quote?.trim() : undefined;
  const sourceName = allowSourcePreview
    ? citation.sourceName?.trim()
    : undefined;
  const cellAnchor = allowSourcePreview
    ? citation.location?.cellAnchor
    : undefined;
  const cellLocation = cellAnchor ? cellAnchorLabel(cellAnchor) : undefined;
  // A page-anchored citation renders the source PDF below the quote. In the
  // default 600px / 420px-tall dialog the page is squeezed into a slit and the
  // cited line lands ~10px tall, which reads as "blurry, can't tell what it
  // points at". Give the document view room; keep the compact box for
  // quote-only citations so an empty dialog is not oversized. A cell-anchored
  // citation renders its sheet there instead, and is gated by the same
  // share-link check: both arms download the underlying source.
  const hasPagePin = Boolean(
    allowSourcePreview &&
    citation.sourceId &&
    (citation.location?.pageNumber || citation.location?.cellAnchor),
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
      <Dialog.Content
        data-testid="comment-knowledge-rule-citation-dialog"
        maxWidth={hasPagePin ? "min(920px, 92vw)" : undefined}
        className={
          hasPagePin
            ? "flex max-h-[85vh] flex-col gap-4 overflow-y-auto"
            : "flex max-h-[min(70vh,420px)] flex-col gap-4 overflow-y-auto"
        }
      >
        <Dialog.Title>
          {t("commentCitationLinks.knowledgeRuleSource")}
        </Dialog.Title>
        <Dialog.Description className="hidden">
          {t("commentCitationLinks.knowledgeRuleExcerpt")}
        </Dialog.Description>
        {statement ? (
          <Text size="2" weight="medium" data-testid="knowledge-rule-statement">
            {statement}
          </Text>
        ) : null}
        {sourceName ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-source-doc"
          >
            {sourceName}
          </Text>
        ) : null}
        {cellLocation ? (
          <Text
            size="2"
            className="text-puntt-neutral-gray-11"
            data-testid="knowledge-rule-citation-cell-location"
          >
            {cellLocation}
          </Text>
        ) : null}
        {quote ? (
          <div
            className="flex flex-col gap-4"
            data-testid="knowledge-rule-citation-quote"
          >
            <Text size="2" className="text-puntt-neutral-gray-11">
              <em>{t("commentCitationLinks.fromKnowledgeDatabase")}</em>
            </Text>
            <Preview text={quote} />
          </div>
        ) : null}
        {hasPagePin && citation.sourceId ? (
          <KnowledgeRuleCitationPin
            sourceId={citation.sourceId}
            pageNumber={citation.location?.pageNumber}
            bbox={citation.location?.bbox}
            relX={citation.location?.rel_x}
            relY={citation.location?.rel_y}
            cellAnchor={cellAnchor}
          />
        ) : null}
        {showKnowledgeDatabaseLink ? (
          <Flex justify="end">
            <Button variant="outline" size="2" asChild>
              <Link
                data-testid="comment-knowledge-rule-citation-view-link"
                to={KNOWLEDGE_DATABASE_PATH}
                search={{
                  ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                  tab: "rules",
                  ruleId: citation.ruleId,
                  ...(citation.sourceId ? { sourceId: citation.sourceId } : {}),
                  // Opens the cited source WHERE the claim was read from -- the
                  // workbook row or the pdf page -- rather than at the top of a
                  // file the reader must then search. One definition, shared
                  // with the panel that resolves it.
                  ...(citationAnchorParam(citation)
                    ? { cite: citationAnchorParam(citation) }
                    : {}),
                }}
                target="_blank"
                rel="noreferrer"
                className="puntt-link flex gap-1"
              >
                <Text>
                  {t("commentCitationLinks.viewRuleInKnowledgeDatabase")}
                </Text>
                <ArrowSquareOut width={16} height={16} />
              </Link>
            </Button>
          </Flex>
        ) : null}
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            {t("commentCitationLinks.close")}
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * `allowQuote: false` is the share-link guest's label: the quote is the
 * verbatim source text the dialog's `allowSourcePreview` gate withholds, so a
 * rule with no rewritten statement falls back to `fallback` instead.
 */
export function knowledgeRuleCitationLabel(
  citation: TicketMessageCitation,
  fallback: string,
  { allowQuote = true }: { allowQuote?: boolean } = {},
): string {
  if (!isKnowledgeRuleCitation(citation)) return fallback;
  return (
    citation.statement?.trim() ||
    (allowQuote ? citation.quote?.trim() : "") ||
    fallback
  );
}
